import { Week } from "../context/linkData.reducer";
import { BlockRanges, EditedSession } from "../context/formData.reducer";
import { isOutsideThreshold } from "./isOutsideThreshold";

/** Every input a week's lock state depends on. */
export interface WeekLockContext {
  thresholdDate: string | null;
  blocks: BlockRanges;
  selectedSessions: EditedSession[];
  originalSelectedWeekIds: { [weekId: string]: string };
  isIntervalsSectionReadOnly: boolean;
  hasDeletedSessionSelected: boolean;
  hasClonedOrNewSessionSelected: boolean;
  canUnassignIntervals: boolean;
  canEditPastSessions: boolean;
}

/**
 * The week already elapsed past the blocking threshold and the user cannot edit
 * past sessions. Exported on its own because the selector needs it apart from
 * the full lock state, to flag the week in its tooltip and highlight.
 */
export const isWeekOutsideEditableThreshold = (week: Week, ctx: WeekLockContext): boolean =>
  !!ctx.thresholdDate &&
  !ctx.canEditPastSessions &&
  isOutsideThreshold(ctx.thresholdDate, week, ctx.blocks);

/**
 * A week is locked - non-interactive in the selector, and kept in the current
 * session when a fork runs - when any of the following hold:
 *
 *  1. A selected session is flagged for deletion.
 *  2. The week is outside the term (`isInTerm` is false).
 *  3. The interval section is read-only and the week cannot be unassigned
 *     (it wasn't originally assigned to a selected session, the user lacks
 *     `can_unassign_intervals`, or a selected session is cloned/new).
 *  4. The week is outside the editable threshold.
 */
export const isWeekLocked = (week: Week, ctx: WeekLockContext): boolean => {
  const canUnassignWeek =
    !!ctx.originalSelectedWeekIds?.[week?.id] &&
    ctx.canUnassignIntervals &&
    !ctx.hasClonedOrNewSessionSelected;

  return (
    ctx.hasDeletedSessionSelected ||
    !week?.isInTerm ||
    (ctx.isIntervalsSectionReadOnly && !canUnassignWeek) ||
    isWeekOutsideEditableThreshold(week, ctx)
  );
};

const withSessionBlocks = (ctx: WeekLockContext, session: EditedSession): WeekLockContext => ({
  ...ctx,
  blocks: session?.blocks ?? ctx.blocks,
});

export const isWeekOutsideEditableThresholdForSession = (
  week: Week,
  session: EditedSession,
  ctx: WeekLockContext,
): boolean => isWeekOutsideEditableThreshold(week, withSessionBlocks(ctx, session));

export const isWeekLockedForSession = (
  week: Week,
  session: EditedSession,
  ctx: WeekLockContext,
): boolean => isWeekLocked(week, withSessionBlocks(ctx, session));

export const isWeekOutsideEditableThresholdForAnySelectedSession = (
  week: Week,
  ctx: WeekLockContext,
): boolean => {
  if (!ctx.selectedSessions?.length) return isWeekOutsideEditableThreshold(week, ctx);

  return ctx.selectedSessions.some(session =>
    isWeekOutsideEditableThresholdForSession(week, session, ctx),
  );
};

export const isWeekLockedForAnySelectedSession = (week: Week, ctx: WeekLockContext): boolean => {
  if (!ctx.selectedSessions?.length) return isWeekLocked(week, ctx);

  return ctx.selectedSessions.some(session => isWeekLockedForSession(week, session, ctx));
};

export const isWeekLockedForAllSelectedSessions = (week: Week, ctx: WeekLockContext): boolean => {
  if (!ctx.selectedSessions?.length) return isWeekLocked(week, ctx);

  return ctx.selectedSessions.every(session => isWeekLockedForSession(week, session, ctx));
};
